import React, { useCallback, useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// EmailJS ids. The public key is meant to live in browser code; sending is
// restricted to this site's domains in the EmailJS dashboard.
const EMAILJS_SERVICE_ID = "service_d9jsb8p";
const EMAILJS_TEMPLATE_ID = "template_hioanmr";
const EMAILJS_PUBLIC_KEY = "TXC8QwGnLmCIcjZ5Z";

const LINKEDIN_URL = "https://www.linkedin.com/in/mohamedoulal/";
const GITHUB_URL = "https://github.com/ElAyachie";
const RESUME_URL = "/Mohamed_Oulal_Resume.pdf";

const TOAST_DURATION_MS = 6000;

const labelClass = "block mb-2 text-base font-medium text-gray-900 dark:text-gray-300";
const inputClass =
  "shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light";
const linkPill =
  "inline-flex items-center gap-2 rounded-full px-4 py-2 text-base font-semibold border-2 border-[#eb4d5c] text-[#eb4d5c] hover:bg-[#eb4d5c] hover:text-white dark:border-[#c9a7ff] dark:text-[#c9a7ff] dark:hover:bg-[#c9a7ff] dark:hover:text-[#0f193d] transition-colors";

const SUCCESS_TOAST = {
  type: "success",
  title: "Message sent",
  message: "Thanks for reaching out — I'll get back to you within a day or two.",
};

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return undefined;
    // Errors stay up longer so there's time to use the LinkedIn fallback.
    const timer = setTimeout(onClose, toast.type === "error" ? TOAST_DURATION_MS * 2 : TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  if (!toast) return null;

  const isSuccess = toast.type === "success";
  const Icon = isSuccess ? CheckCircleOutlineIcon : ErrorOutlineIcon;

  return (
    <div
      role={isSuccess ? "status" : "alert"}
      aria-live={isSuccess ? "polite" : "assertive"}
      className="fixed z-50 bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm sm:left-auto sm:right-6 sm:translate-x-0"
    >
      <div
        className={
          "flex items-start gap-3 p-4 rounded-lg shadow-lg border text-left animate-[toastIn_200ms_ease-out] bg-white text-gray-900 dark:bg-gray-800 dark:text-white " +
          (isSuccess ? "border-green-200 dark:border-green-700" : "border-red-200 dark:border-red-700")
        }
      >
        <Icon
          className={isSuccess ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
          fontSize="small"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold">{toast.title}</p>
          <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
            {toast.message}
            {toast.action && (
              <>
                {" "}
                <a
                  href={toast.action.href}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#eb4d5c] dark:text-[#c9a7ff] hover:underline"
                >
                  {toast.action.label}
                </a>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss notification"
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

const ContactForm = () => {
  const form = useRef();
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const closeToast = useCallback(() => setToast(null), []);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (sending) return;

    const data = new FormData(form.current);

    // Honeypot: people never see this field, bots fill it in. Pretend it
    // worked so the bot moves on, but don't send anything.
    if ((data.get("website") || "").trim()) {
      form.current.reset();
      setToast(SUCCESS_TOAST);
      return;
    }

    const fromName = (data.get("from_name") || "").trim();
    const fromEmail = (data.get("from_email") || "").trim();
    const subject = (data.get("subject") || "").trim();
    const message = (data.get("message") || "").trim();

    // Send the common variable names so the EmailJS template always has a
    // valid sender, reply-to address and name.
    const templateParams = {
      from_name: fromName,
      name: fromName,
      from_email: fromEmail,
      email: fromEmail,
      user_email: fromEmail,
      reply_to: fromEmail,
      subject,
      title: subject,
      message,
    };

    setSending(true);
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      form.current.reset();
      setToast(SUCCESS_TOAST);
    } catch (error) {
      console.error("Contact form failed to send:", error?.status, error?.text || error);
      setToast({
        type: "error",
        title: "Message not sent",
        message: "Something went wrong on my end. Please try again, or",
        action: { href: LINKEDIN_URL, label: "message me on LinkedIn." },
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="content">
      <section className="h-full">
        <div className="py-8 lg:pb-8 pt-5 px-4 mx-auto max-w-screen-md">
          <h2 className="mb-4 text-4xl font-extrabold text-center text-gray-900 dark:text-white">Contact Me</h2>
          <p className="mb-5 font-light text-center text-gray-500 dark:text-gray-400 sm:text-xl">
            Recruiting, collaborating, or just want to talk healthcare tech? Send me a note — I usually reply within a
            day or two.
          </p>

          <div className="mb-8 flex flex-wrap justify-center gap-3">
            <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className={linkPill}>
              <LinkedInIcon fontSize="small" aria-hidden="true" />
              LinkedIn
            </a>
            <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={linkPill}>
              <GitHubIcon fontSize="small" aria-hidden="true" />
              GitHub
            </a>
            <a href={RESUME_URL} target="_blank" rel="noreferrer" className={linkPill}>
              <DescriptionOutlinedIcon fontSize="small" aria-hidden="true" />
              Resume
            </a>
          </div>

          <form ref={form} onSubmit={sendEmail} className="relative space-y-8" id="emailForm">
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Your name
                </label>
                <input name="from_name" type="text" id="name" autoComplete="name" maxLength={100} className={inputClass} required />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Your email
                </label>
                <input name="from_email" type="email" id="email" autoComplete="email" className={inputClass} required />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className={labelClass}>
                Subject
              </label>
              <input name="subject" type="text" id="subject" maxLength={200} className={inputClass + " p-3"} required />
            </div>
            <div>
              <label htmlFor="message" className={labelClass}>
                Your message
              </label>
              <textarea name="message" id="message" rows="6" maxLength={5000} className={inputClass} required />
            </div>

            {/* Honeypot for spam bots: hidden from people and screen readers. */}
            <div aria-hidden="true" className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden">
              <label htmlFor="website">Website</label>
              <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="py-3 px-5 text-base font-medium text-center text-white rounded-lg bg-[#DC4441] sm:w-fit hover:bg-[#d72926] focus:ring-4 focus:outline-none focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
};

export default ContactForm;
